/**
 * Popout window context — boot-time inspection of `?popout=` URL params.
 *
 * Each popout window loads the same SPA with query params telling it
 * what to render and which project to render it for. This module is the
 * single source of truth for those params; importing it elsewhere keeps
 * the URL parsing out of every consumer.
 *
 * Also exposes `getCurrentWindowLabel()` — popouts pass their own window
 * label into host-browser ids so a popout window's embedded browsers are
 * namespaced separately from "main" (and don't collide with it).
 */

export type PopoutKind = 'browser' | 'terminal' | 'files';

export interface PopoutContext {
  /** True when this React tree is rendering inside a popout window. */
  isPopout: boolean;
  kind: PopoutKind | null;
  projectId: string | null;
  /** Tauri window label assigned at popout_open time. Defaults to "main"
   *  for the main window. */
  windowLabel: string;
}

let cached: PopoutContext | null = null;

function parse(): PopoutContext {
  if (cached) return cached;
  if (typeof window === 'undefined') {
    cached = { isPopout: false, kind: null, projectId: null, windowLabel: 'main' };
    return cached;
  }
  const params = new URLSearchParams(window.location.search);
  const rawKind = params.get('popout');
  const projectId = params.get('projectId');
  const windowLabel = params.get('windowLabel') ?? 'main';
  const kind: PopoutKind | null = rawKind === 'browser' || rawKind === 'terminal' || rawKind === 'files' ? rawKind : null;
  cached = {
    isPopout: kind !== null,
    kind,
    projectId,
    windowLabel,
  };
  return cached;
}

export function getPopoutContext(): PopoutContext {
  return parse();
}

export function getCurrentWindowLabel(): string {
  return parse().windowLabel;
}
